/* eslint-disable dot-notation */
/* eslint-disable quote-props */
import * as XLSX from 'xlsx';
import FormSection from '../pages/forms/pexbd_v2/models/FormSection';
import FormSubsection from '../pages/forms/pexbd_v2/models/FormSubsection';
import Question from '../pages/forms/pexbd_v2/models/Question';
import QuestionSlot from '../pages/forms/pexbd_v2/models/QuestionSlot';
import SlotTypes from '../pages/forms/pexbd_v2/models/SlotTypes';
import { getFormSpecRegistryForKey, isDynamicFormSpecKey } from '../pages/forms/pexbd_v2/models/formSpecRegistries';

/**
 * Fréquence de mise à jour d'un indicateur. La valeur enregistrée dans le
 * formulaire n'est pas fiable : on lit la fréquence depuis la spec (via le
 * `specKey` du formulaire et l'`id` stable de la question), avec repli sur la
 * valeur enregistrée si la spec est introuvable.
 */
const resolveFrequency = (formData: any, question: Question): number => {
  const specKey = formData?.specKey;
  if (isDynamicFormSpecKey(specKey ?? null)) {
    try {
      const specQuestion = getFormSpecRegistryForKey(specKey).getQuestion(question.id);
      if (specQuestion?.frequency != null) return specQuestion.frequency;
    } catch (e) {
      // spec introuvable : on retombe sur la valeur enregistrée
    }
  }
  return question.frequency;
};

/**
 * Prochaine année de mise à jour d'un indicateur.
 *
 * On part de la première année étudiée (année d'adhésion) et on ajoute des
 * multiples de la fréquence jusqu'à atteindre une année >= année de référence
 * (par défaut l'année courante). Si la dernière mise à jour tombe pile sur
 * l'année de référence, on l'affiche; sinon on affiche la prochaine à venir.
 */
const getNextUpdateYear = (firstStudiedYear: number, frequency: number, referenceYear: number): number => {
  if (!frequency || frequency <= 0) return referenceYear;
  if (firstStudiedYear >= referenceYear) return firstStudiedYear;
  const elapsed = referenceYear - firstStudiedYear;
  const periods = Math.ceil(elapsed / frequency);
  return firstStudiedYear + periods * frequency;
};

const DETAILED_HEADERS = [
  'Municipalité',
  'Année Étudié',
  'Thématique',
  'ID',
  'Indicateur',
  'Type d\'indicateur',
  'Données',
  'Unité',
  'Pointage',
  'Pointage MAX',
  'Cibles des EM associé',
  'Commentaires et sources des données',
  'Fréquence',
  'Prochaine mise à jour',
  'Année adhésion',
  'Méthode de pointage',
];

class ExportService {
  static exportFormCSV = async (headers: string[], data: any[], fileName: string) => {
    // Create a new workbook and a worksheet
    const workbook = XLSX.utils.book_new();

    // Add a new row for headers
    const worksheetData = [headers];

    // Append each row of data
    data.forEach((row) => {
      worksheetData.push(headers.map((header) => row[header] || ''));
    });

    // Create a worksheet with the data
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(workbook, ws, 'Data');

    // Write the CSV file
    XLSX.writeFile(workbook, `${fileName}.csv`);
  };

  /**
   * Rapport détaillé : une ligne par créneau (slot) de chaque indicateur,
   * pour l'ensemble des formulaires fournis.
   */
  static exportDetailedCSV = async (
    forms: any[],
    fileName: string,
    adhesionYearByMunicipalityId: Map<string, number> = new Map(),
  ) => {
    const allForms: any[][] = forms.map((formData: any) => {
      const rows: any[] = [];

      // L'année d'adhésion fiable provient de la municipalité; on retombe sur
      // celle enregistrée dans le formulaire si elle est absente.
      const adhesionYear = adhesionYearByMunicipalityId.get(formData.municipalityId)
        ?? parseInt(formData.anneeAdhesion, 10);

      (formData.sections ?? []).forEach((section: FormSection) => {
        section.subsections.forEach((subsection: FormSubsection) => {
          subsection.questions.forEach((question: Question) => {
            const frequency = resolveFrequency(formData, question);
            question.slots.forEach((slot: QuestionSlot) => {
              const timeSinceUpdate = (parseInt(formData.anneeEtudiee, 10) - adhesionYear) % frequency;
              const timeToNextUpdate = frequency - timeSinceUpdate;

              const methodString = slot.help.method.length > 0 ? slot.help.method?.map((obj: any) => {
                const [key, value] = Object.entries(obj)[0];
                return `${key} : ${value} pt`;
              }).join('; ') : '';

              let donnees = slot.data[0];
              if (slot.type === SlotTypes.REPEATER) {
                let total = 0;
                slot.data.forEach((v: any) => {
                  total += parseFloat(v.amount) || 0;
                });
                total = Math.round(total * 100) / 100;
                const perCapita = (total / formData['population']).toFixed(2);
                donnees = perCapita;
              }

              const { score } = slot;

              let scoreTemp = score;
              if (slot.type === SlotTypes.GEOMATIQUE) {
                scoreTemp = '';
              } else {
                scoreTemp = parseFloat(score).toLocaleString('fr-FR');
              }

              let indicatorType = slot.type;
              if (indicatorType === SlotTypes.REPEATER) {
                indicatorType = SlotTypes.ETOILES;
              }

              const row: any = {};
              row['Municipalité'] = formData.nomMunicipalite;
              row['Année Étudié'] = parseInt(formData.anneeEtudiee, 10);
              row['Thématique'] = section.title;
              row['ID'] = `${question.number.toString()}\u00A0`;
              row['Indicateur'] = question.title;
              row['Type d\'indicateur'] = `${question.type}-${indicatorType}`;
              row['Données'] = donnees;
              row['Unité'] = slot.unit;
              row['Pointage'] = scoreTemp;
              row['Pointage MAX'] = slot.maxScore;
              row['Cibles des EM associé'] = question.idEM;
              row['Commentaires et sources des données'] = question.comments;
              row['Fréquence'] = frequency;
              row['Prochaine mise à jour'] = parseInt(formData.anneeEtudiee, 10) + timeToNextUpdate;
              row['Année adhésion'] = adhesionYear;
              row['Méthode de pointage'] = methodString;

              rows.push(row);
            });
          });
        });
      });

      return rows;
    });

    const combinedRows = ([] as any[]).concat(...allForms);
    await ExportService.exportFormCSV(DETAILED_HEADERS, combinedRows, fileName);
  };

  /**
   * Rapport « prochaines mises à jour » : une ligne par municipalité, une
   * colonne « Première année étudiée » puis une colonne par indicateur
   * affichant l'année de sa prochaine mise à jour.
   *
   * Si plusieurs formulaires existent pour une même municipalité, on retient
   * celui dont l'année étudiée est la plus récente (le plus complet en termes
   * d'indicateurs disponibles).
   */
  static exportNextUpdateMatrixCSV = async (
    forms: any[],
    fileName: string,
    adhesionYearByMunicipalityId: Map<string, number> = new Map(),
    referenceYear: number = new Date().getFullYear() - 1,
  ) => {
    // Regrouper par municipalité en gardant le formulaire le plus récent.
    const latestByMunicipality = new Map<string, any>();
    forms.forEach((formData: any) => {
      const key = formData.nomMunicipalite ?? formData.municipalityId;
      if (!key) return;
      const existing = latestByMunicipality.get(key);
      if (!existing || parseInt(formData.anneeEtudiee, 10) > parseInt(existing.anneeEtudiee, 10)) {
        latestByMunicipality.set(key, formData);
      }
    });

    // Déterminer l'ordre des colonnes d'indicateurs à partir de l'ensemble
    // des formulaires (union des ids), en préservant l'ordre section/question.
    const questionColumns: { id: string; label: string }[] = [];
    const seen = new Set<string>();
    latestByMunicipality.forEach((formData: any) => {
      (formData.sections ?? []).forEach((section: FormSection) => {
        section.subsections.forEach((subsection: FormSubsection) => {
          subsection.questions.forEach((question: Question) => {
            const id = question.number?.toString() ?? question.id;
            if (seen.has(id)) return;
            seen.add(id);
            questionColumns.push({
              id,
              label: `${id} (${resolveFrequency(formData, question)} ans)`,
            });
          });
        });
      });
    });

    const firstYearHeader = 'Première année étudiée';
    const headers = ['Municipalité', firstYearHeader, ...questionColumns.map((c) => c.label)];

    const rows: any[] = [];
    latestByMunicipality.forEach((formData: any) => {
      // L'année d'adhésion fiable provient de la municipalité; on retombe sur
      // celle enregistrée dans le formulaire si elle est absente.
      const municipalityAdhesionYear = adhesionYearByMunicipalityId.get(formData.municipalityId);
      const firstStudiedYear = municipalityAdhesionYear ?? parseInt(formData.anneeAdhesion, 10);
      const row: any = {};
      row['Municipalité'] = formData.nomMunicipalite;
      row[firstYearHeader] = firstStudiedYear;

      const nextUpdateById = new Map<string, number>();
      (formData.sections ?? []).forEach((section: FormSection) => {
        section.subsections.forEach((subsection: FormSubsection) => {
          subsection.questions.forEach((question: Question) => {
            const id = question.number?.toString() ?? question.id;
            nextUpdateById.set(id, getNextUpdateYear(firstStudiedYear, resolveFrequency(formData, question), referenceYear));
          });
        });
      });

      questionColumns.forEach((col) => {
        const value = nextUpdateById.get(col.id);
        row[col.label] = value ?? '';
      });

      rows.push(row);
    });

    // Trier par nom de municipalité pour un rapport lisible.
    rows.sort((a, b) => String(a['Municipalité']).localeCompare(String(b['Municipalité']), 'fr'));

    await ExportService.exportFormCSV(headers, rows, fileName);
  };
}

export default ExportService;
